// Round ORG-2 (2026) — client-side CSV export for the Organizational Chart.
//
// Why client-side: the export must respect the CURRENT view (focus root,
// department filter, search) and must never expose more than the caller was
// already authorized to receive — building the file from the exact rows the
// user is looking at guarantees both, with no new endpoint and no new
// package. (Image/PDF export of the rendered chart is documented as a
// follow-up; CSV/Excel-openable output was the round-1 priority per plan.)
//
// Columns: name, EmpID, title, department, supervisor, direct reports,
// total reports, email, extension — all fields already present in the
// authorized GET /api/OrgChart payload. Nothing HR-only is included.

const csvEscape = (v) => {
    const s = String(v ?? "");
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * rows: [{ name, empId, title, departmentName, supervisorName,
 *          directReports, totalReports, email, extension }]
 */
export function exportOrgRowsToCsv(rows, filename = "org-chart.csv") {
    const header = [
        "Name", "EmpID", "Title", "Department", "Supervisor",
        "Direct Reports", "Total Reports", "Email", "Extension",
    ];
    const lines = [header.join(",")];
    (rows || []).forEach((r) => {
        lines.push([
            csvEscape(r.name),
            csvEscape(r.empId),
            csvEscape(r.title),
            csvEscape(r.departmentName),
            csvEscape(r.supervisorName),
            csvEscape(r.directReports ?? ""),
            csvEscape(r.totalReports ?? ""),
            csvEscape(r.email),
            csvEscape(r.extension),
        ].join(","));
    });
    // BOM so Excel opens UTF-8 names correctly.
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Length-of-service string from a hire date (legacy LoSTextBox parity). */
export function lengthOfService(hireDate) {
    if (!hireDate) return null;
    const hire = new Date(hireDate);
    if (isNaN(hire.getTime()) || hire <= new Date("1970-01-02")) return null;
    const now = new Date();
    if (hire > now) return "0 Days";
    let years = now.getFullYear() - hire.getFullYear();
    let months = now.getMonth() - hire.getMonth();
    let days = now.getDate() - hire.getDate();
    if (days < 0) {
        months -= 1;
        days += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
    }
    if (months < 0) { years -= 1; months += 12; }
    const parts = [];
    if (years > 0) parts.push(`${years} Year${years === 1 ? "" : "s"}`);
    if (months > 0) parts.push(`${months} Month${months === 1 ? "" : "s"}`);
    parts.push(`${days} Day${days === 1 ? "" : "s"}`);
    return parts.join(", ");
}
